/**
 * /pipeline-facility/dig-reports — the dig report register and
 * /pipeline-facility/dig-reports/:id — one report (fieldiq-api#1219, PF-3a).
 *
 * A report is drafted from its dig (dig page → Create draft report); this
 * register lists them. The report page shows what the report says — the live
 * `preview` on a draft, the frozen `content` on an issued one — with the same
 * card as the dig page (edit draft, issue, PDF).
 */
import React from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { FileText } from 'lucide-react';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { displayValue } from '../../../features/fieldiq-handoff/contracts';
import { usePfReport, usePfReports } from '../../../hooks/usePfDigs';
import { usePfMeta } from '../../../hooks/usePipelineFacility';
import { formatInstantDay } from '../../../lib/calendar-date';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { canDispatchPf, fmtNumber, pfDay } from '../../../lib/pipeline-facility';
import { pfDigStatusSkin, pfReportStatusSkin } from '../../../lib/pf-digs';
import type { PfReport, PfReportContent } from '../../../types/pf-digs';
import { useBackToList, useOpenRecord } from '../../downhole/recordPage';
import { isNotFound, PF_EYEBROW, PfBadge, PfChip, PfChipRow, PfEmpty, PfLoading, PfPanel, PfRegister, PfSpec, type PfColumn } from '../PfBits';
import { ReportPanelBody } from './ReportPanel';

export default function PfReportsScreen() {
  const navigate = useNavigate();
  const openRecord = useOpenRecord();
  const [searchParams, setSearchParams] = useSearchParams();
  const status = searchParams.get('status') ?? '';
  const catalog = usePfMeta().data?.data ?? null;
  const listQuery = usePfReports(status ? { status } : undefined);
  const allQuery = usePfReports();
  const rows = listQuery.data?.data ?? [];
  const all = allQuery.isLoading || queryLoadFailure(allQuery) ? undefined : allQuery.data?.data;
  const countOf = (pick: (r: PfReport) => boolean) => displayValue(all ? all.filter(pick).length : undefined);
  const setStatus = (s: string) => {
    const p = new URLSearchParams(searchParams);
    if (s) p.set('status', s);
    else p.delete('status');
    setSearchParams(p, { replace: true });
  };
  const columns: PfColumn<PfReport>[] = [
    { key: 'number', header: 'Report', render: (r) => <span className="font-mono font-extrabold text-white">{r.reportNumber || 'Draft'}</span> },
    { key: 'dig', header: 'Dig', render: (r) => (<><p className="font-mono">{r.digNumber || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{pfDigStatusSkin(r.digStatus).label}</p></>) },
    { key: 'job', header: 'Job', render: (r) => (<><p className="font-mono">{r.jobNumber || '—'}</p><p className="mt-0.5 text-xs text-fieldiq-muted">{r.customerName || '—'}</p></>) },
    { key: 'title', header: 'Title', render: (r) => r.title || '—' },
    { key: 'signed', header: 'Signed by', render: (r) => r.issuedSignatoryName || '—' },
    { key: 'status', header: 'Status', render: (r) => <PfBadge skin={pfReportStatusSkin(r.status)} value={r.status} /> },
  ];
  return (
    <div data-testid="pf-reports-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title="Dig reports"
        description="The report for each dig — drafted from the dig, issued once it is backfilled."
        onBack={() => navigate(-1)}
        backLabel="Back"
        stats={[
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Reports', tone: 'neutral', loading: allQuery.isLoading, value: countOf(() => true) },
          { icon: <FileText className="size-4" aria-hidden="true" />, label: 'Drafts', tone: 'accent', loading: allQuery.isLoading, value: countOf((r) => r.status === 'draft') },
        ]}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          <PfChipRow label="Filter by status">
            <PfChip label="All" active={!status} onClick={() => setStatus('')} count={allQuery.isLoading ? undefined : countOf(() => true)} />
            {(catalog?.reportStatuses ?? [{ value: 'draft', label: 'Draft' }, { value: 'issued', label: 'Issued' }]).map((s) => (
              <PfChip key={s.value} label={s.label} active={status === s.value} onClick={() => setStatus(s.value)} count={allQuery.isLoading ? undefined : countOf((r) => r.status === s.value)} />
            ))}
          </PfChipRow>
          <PfPanel eyebrow="Report register" title="Dig reports" testId="pf-report-register">
            <PfRegister
              query={listQuery}
              rows={rows}
              columns={columns}
              rail={(r) => pfReportStatusSkin(r.status).rail}
              onOpen={(r) => openRecord(`/pipeline-facility/dig-reports/${r.id}`)}
              subject="dig reports"
              testId="pf-report"
              minWidth="min-w-[900px]"
              empty={status ? <PfEmpty>No report with this status.</PfEmpty> : <PfEmpty>No dig reports yet. Draft one from a dig.</PfEmpty>}
              card={(r) => (
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate font-mono text-sm font-extrabold text-white">{r.reportNumber || 'Draft'}</p>
                    <p className="mt-0.5 text-xs text-fieldiq-muted">{r.digNumber || '—'} · {r.jobNumber || '—'}</p>
                  </div>
                  <PfBadge skin={pfReportStatusSkin(r.status)} value={r.status} />
                </div>
              )}
            />
          </PfPanel>
        </div>
      </section>
    </div>
  );
}

const num = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v)) ? Number(v) : null);
const str = (v: unknown) => (typeof v === 'string' ? v : null);
const text = (v: unknown) => (v === null || v === undefined || v === '' ? '—' : String(v));

/** What the report says, read from `preview` / `content` — shown as written, nothing recomputed. */
function ContentView({ content, timeZone }: { content: PfReportContent; timeZone: string | null | undefined }) {
  const dig = (content.dig ?? {}) as Record<string, unknown>;
  const units = content.units ?? {};
  const features = Array.isArray(content.features) ? content.features : [];
  const repairs = Array.isArray(content.repairs) ? content.repairs : [];
  return (
    <div className="space-y-4 px-4 py-4" data-testid="pf-report-content">
      <dl className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PfSpec label="Dig" value={text(dig.digNumber)} mono />
        <PfSpec label="Chainage" value={`${fmtNumber(num(dig.chainage))}${dig.chainage != null && units.chainage ? ` ${units.chainage}` : ''}`} mono />
        <PfSpec label="Excavated" value={pfDay(str(dig.excavatedOn))} />
        <PfSpec label="Backfilled" value={pfDay(str(dig.backfilledOn))} />
      </dl>
      <div>
        <p className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Features ({features.length})</p>
        {features.length === 0 ? (
          <p className="text-sm text-fieldiq-text">None recorded.</p>
        ) : (
          <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
            {features.map((f, i) => (
              <li key={i} className="flex flex-wrap gap-3 px-3 py-2 text-sm text-fieldiq-text">
                <span className="font-mono font-bold text-white">{text(f.anomalyFeatureId ?? f.featureTypeLabel)}</span>
                <span>{text(f.featureTypeLabel)}</span>
                <span>Assessment {text(f.assessmentResultLabel)}</span>
                <span className="ml-auto font-mono">Remaining WT {f.remainingWtPct == null ? '—' : `${text(f.remainingWtPct)}%`}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <div>
        <p className="mb-2 text-[0.62rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Repairs ({repairs.length})</p>
        {repairs.length === 0 ? (
          <p className="text-sm text-fieldiq-text">None recorded.</p>
        ) : (
          <ul className="divide-y divide-fieldiq-border rounded-fi-control border border-fieldiq-border">
            {repairs.map((r, i) => (
              <li key={i} className="flex flex-wrap gap-3 px-3 py-2 text-sm text-fieldiq-text">
                <span className="font-bold text-white">{text(r.methodLabel ?? r.method)}</span>
                <span>{text(r.material)}</span>
                <span>{pfDay(str(r.repairedOn))}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      {timeZone && <p className="text-xs text-fieldiq-muted">Times on the PDF are in {timeZone}.</p>}
    </div>
  );
}

export function PfReportDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList('/pipeline-facility/dig-reports');
  const { user } = useAuth();
  const query = usePfReport(id);
  const report = query.data?.data ?? null;
  const meta = query.data?.meta ?? null;
  const failure = queryLoadFailure(query);
  const content = report?.status === 'issued' ? report.content : report?.preview;
  return (
    <div data-testid="pf-report-detail-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title={report?.reportNumber || (report ? 'Draft dig report' : query.isLoading ? 'Dig report' : '—')}
        description={report ? `${report.digNumber ?? '—'} · ${report.jobNumber ?? '—'} · ${report.customerName ?? '—'}` : undefined}
        onBack={back}
        backLabel="Back"
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4">
          {query.isLoading ? (
            <PfLoading label="Loading report" />
          ) : isNotFound(queryFailureError(query)) || (!failure && !report) ? (
            <PfPanel eyebrow="Dig report" title="Dig report">
              <PfEmpty>This report was not found. A deleted draft is gone.</PfEmpty>
            </PfPanel>
          ) : failure || !report ? (
            <PfPanel eyebrow="Dig report" title="Dig report">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this report" />
            </PfPanel>
          ) : (
            <>
              <PfPanel eyebrow="Dig report" title={report.title || 'Untitled'} testId="pf-report-detail">
                <ReportPanelBody report={report} canWrite={canDispatchPf(user?.role)} showOpenLink={false} timeZone={meta?.timeZone} />
                <p className="border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">
                  Dig <Link className="underline" to={`/pipeline-facility/digs/${report.digId}`}>{report.digNumber ?? '—'}</Link>
                  {report.issuedAt ? ` · issued ${formatInstantDay(report.issuedAt, meta?.timeZone) ?? '—'}` : ' · not issued'}
                </p>
              </PfPanel>
              {content ? (
                <PfPanel eyebrow={report.status === 'issued' ? 'As issued' : 'Live preview — changes as the dig is worked'} title="What the report says">
                  <ContentView content={content} timeZone={content.timeZone ?? meta?.timeZone} />
                </PfPanel>
              ) : null}
            </>
          )}
        </div>
      </section>
    </div>
  );
}
